<template>
  <div ref="root" class="product-scroll-controller">
    <slot />
  </div>
</template>
<script setup>
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import Cookies from 'js-cookie'
import useEmitter from '@composables/useEmitter'

gsap.registerPlugin(ScrollTrigger)

const root = ref(null)
const emitter = useEmitter()

const PIN_RELEASE_DISTANCE = 20000

let ctx
let resizeTimer
let mountFrame
let hasPlayedIntro = false
let isIntroPlaying = false
let hasAgeGatePassed = Boolean(Cookies.get('ofage'))
let pendingIntroPlay = null
let isActive = false
let lastViewportWidth = 0
let lastViewportHeight = 0

function flushPendingIntroPlay() {
  hasAgeGatePassed = true

  if (!pendingIntroPlay) return

  const play = pendingIntroPlay
  pendingIntroPlay = null
  play()
}

function queueIntroPlay(play) {
  if (hasAgeGatePassed) {
    play()
    return
  }

  pendingIntroPlay = () => {
    if (!isActive || hasPlayedIntro) return

    ctx?.add(play)
  }
}

function getViewportHeight() {
  return window.visualViewport?.height ?? window.innerHeight
}

function setIntroEntranceState(productHolder, introColumns, bottleLoader, bgCloud) {
  gsap.set(productHolder, {
    autoAlpha: 0,
  })

  gsap.set(introColumns, {
    autoAlpha: 0,
    y: 22,
    scale: 0.96,
    transformOrigin: '50% 50%',
  })

  gsap.set(bottleLoader, {
    autoAlpha: 0,
  })

  gsap.set(bgCloud, {
    scale: 1.12,
  })
}

function playIntroEntrance(productHolder, introColumns, bottleLoader, bgCloud) {
  if (isIntroPlaying || hasPlayedIntro) return

  isIntroPlaying = true

  const introTimeline = gsap.timeline({
    defaults: {
      duration: 0.75,
      ease: 'power3.out',
    },
    onComplete: () => {
      hasPlayedIntro = true
      isIntroPlaying = false
      gsap.set([productHolder, bottleLoader], {
        clearProps: 'transform,willChange',
      })
    },
  })

  setIntroEntranceState(productHolder, introColumns, bottleLoader, bgCloud)

  introTimeline
    .to(bgCloud, {
      scale: 1,
      duration: 1.6,
      ease: 'sine.out',
    }, 0)
    .to(productHolder, {
      autoAlpha: 1,
      duration: 0.9,
      ease: 'sine.out',
    }, 0.14)
    .to(bottleLoader, {
      autoAlpha: 1,
      duration: 0.9,
      ease: 'sine.out',
    }, 0.42)
    .to(introColumns, {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      stagger: 0.045,
    }, 0.52)
}

function setIntroFinalState(productHolder, introColumns, bottleLoader, bgCloud) {
  gsap.set([productHolder, bottleLoader], {
    autoAlpha: 1,
    clearProps: 'transform,willChange',
  })

  gsap.set(introColumns, {
    autoAlpha: 1,
    y: 0,
    scale: 1,
  })

  gsap.set(bgCloud, {
    scale: 1,
  })
}

function buildScene() {
  if (!isActive || !root.value || isIntroPlaying) return

  ctx?.revert()

  ctx = gsap.context(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let viewportHeight = getViewportHeight()
    const article = root.value.querySelector('.product')
    const intro = article?.querySelector(':scope > .intro')
    const productHolder = intro?.querySelector('.product-holder')
    const bgCloud = intro?.querySelector('.bg .clouds')
    const introColumns = gsap.utils.toArray(intro?.querySelectorAll('.grid-container > .col') || [])
    const bottleLoader = intro?.querySelector('bottle-loader, .canvas-shell, .bottle-loader__transition-fallback')
    const nextScene = root.value.querySelector('.water-scene')
    const vibeSection = nextScene?.querySelector('.vibe')
    const vibeContainer = vibeSection?.querySelector('.container')
    const vibeCards = gsap.utils.toArray(vibeSection?.querySelectorAll('.swiper-slide') || [])
    const videoSection = root.value.querySelector('.video-module')
    const wtbSection = root.value.querySelector('.wtb-container')
    const waterCopy = nextScene?.querySelector('.water-copy')
    const body = document.body

    if (
      !article ||
      !intro ||
      !productHolder ||
      !bgCloud ||
      !introColumns.length ||
      !bottleLoader ||
      !nextScene ||
      !vibeSection ||
      !vibeContainer ||
      !videoSection
    ) {
      return
    }

    gsap.set(root.value, {
      position: 'relative',
      display: 'block',
      width: '100%',
    })

    gsap.set([intro, nextScene, videoSection, wtbSection].filter(Boolean), {
      position: 'relative',
      width: '100%',
    })

    gsap.set(intro, {
      zIndex: 1,
    })

    gsap.set(nextScene, {
      zIndex: 2,
    })

    gsap.set(videoSection, {
      zIndex: 3,
    })

    gsap.set(wtbSection, {
      zIndex: 4,
    })

    gsap.set(bgCloud, {
      willChange: 'transform',
    })

    gsap.set([productHolder, bottleLoader], {
      willChange: 'opacity',
    })

    gsap.set(introColumns, {
      force3D: true,
      willChange: 'transform, opacity',
    })

    if (prefersReducedMotion || hasPlayedIntro) {
      setIntroFinalState(productHolder, introColumns, bottleLoader, bgCloud)
    } else {
      setIntroEntranceState(productHolder, introColumns, bottleLoader, bgCloud)
      queueIntroPlay(() => playIntroEntrance(productHolder, introColumns, bottleLoader, bgCloud))
    }

    const createPanelPin = (
      panel,
      id,
      pinSpacing = false,
      getEndDistance = () => PIN_RELEASE_DISTANCE,
      start = () => {
        const panelHeight = Math.max(panel.offsetHeight || 0, panel.scrollHeight || 0)
        return panelHeight < viewportHeight ? 'top top' : 'bottom bottom'
      },
    ) => {
      ScrollTrigger.create({
        id,
        trigger: panel,
        start,
        end: () => `+=${getEndDistance()}`,
        pin: true,
        pinSpacing,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        refreshPriority: 1,
        onRefreshInit: () => {
          viewportHeight = getViewportHeight()
        },
      })
    }

    createPanelPin(
      intro,
      'product-intro-pin',
      false,
      () => Math.max(Math.round(intro.offsetHeight || getViewportHeight()), getViewportHeight()),
    )

    createPanelPin(nextScene, 'product-overlap-vibe')
    createPanelPin(videoSection, 'product-overlap-video')

    if (prefersReducedMotion) {
      root.value.classList.remove('scroll-prep')
      return
    }

    gsap.set(vibeContainer, {
      autoAlpha: 1,
      yPercent: 0,
      force3D: true,
      willChange: 'transform, opacity',
    })

	if(vibeCards.length){
		gsap.set(vibeCards, {
		autoAlpha: 0,
		y: 34,
		scale: 0.94,
		transformOrigin: '50% 50%',
		force3D: true,
		willChange: 'transform, opacity',
		})

		vibeCards.forEach((card, index) => {
		gsap.timeline({
			scrollTrigger: {
			id: `product-vibe-card-${index}`,
			trigger: card,
			start: 'top 88%',
			end: 'top 58%',
			scrub: true,
			invalidateOnRefresh: true,
			},
		}).to(card, {
			autoAlpha: 1,
			y: 0,
			scale: 1,
			ease: 'none',
		})
		})
	}

    root.value.classList.remove('scroll-prep')

    gsap.timeline({
      scrollTrigger: {
        id: 'product-vibe-video-fade',
        trigger: videoSection,
        start: 'top bottom',
        end: 'top top',
        scrub: true,
        invalidateOnRefresh: true,
      },
    })
      .to(vibeContainer, {
        autoAlpha: 0,
        yPercent: -8,
        ease: 'none',
      }, 0)

    const backgroundTargets = [body, videoSection, waterCopy].filter(Boolean)
    const setApplyVideoBackground = (shouldApply) => {
      backgroundTargets.forEach((target) => {
        target.classList.toggle('apply-lightred-bg', shouldApply)
      })
    }

    ScrollTrigger.create({
      id: 'product-video-apply-bg',
      trigger: videoSection,
      start: 'top top',
      end: 'bottom top',
      onEnter: () => setApplyVideoBackground(true),
      onEnterBack: () => setApplyVideoBackground(true),
      onLeaveBack: () => setApplyVideoBackground(false),
    })

  }, root.value)
}

function handleResize() {
  if (isIntroPlaying) return

  const nextViewportWidth = window.visualViewport?.width ?? window.innerWidth
  const nextViewportHeight = window.visualViewport?.height ?? window.innerHeight

  const widthDelta = Math.abs(nextViewportWidth - lastViewportWidth)
  const heightDelta = Math.abs(nextViewportHeight - lastViewportHeight)

  lastViewportWidth = nextViewportWidth
  lastViewportHeight = nextViewportHeight

  if (widthDelta < 1 && heightDelta < 120) {
    return
  }

  window.clearTimeout(resizeTimer)

  resizeTimer = window.setTimeout(() => {
    buildScene()
  }, 160)
}

onMounted(async () => {
  await nextTick()
  isActive = true
  hasAgeGatePassed = Boolean(Cookies.get('ofage'))
  emitter.on('ofage', flushPendingIntroPlay)

  lastViewportWidth = window.visualViewport?.width ?? window.innerWidth
  lastViewportHeight = window.visualViewport?.height ?? window.innerHeight

  mountFrame = requestAnimationFrame(() => {
    mountFrame = requestAnimationFrame(buildScene)
  })

  window.addEventListener('resize', handleResize, { passive: true })
  window.addEventListener('orientationchange', handleResize, { passive: true })
  window.visualViewport?.addEventListener('resize', handleResize, { passive: true })
})

onBeforeUnmount(() => {
  isActive = false
  isIntroPlaying = false

  window.clearTimeout(resizeTimer)
  window.cancelAnimationFrame(mountFrame)

  window.removeEventListener('resize', handleResize)
  window.removeEventListener('orientationchange', handleResize)
  window.visualViewport?.removeEventListener('resize', handleResize)
  emitter.off('ofage', flushPendingIntroPlay)

  pendingIntroPlay = null
  ctx?.revert()
})
</script>
